import type { SVGProps } from 'react';
import {
  LOCKUP,
  MARK_LEAVES,
  MARK_STAR,
  SMALL_LEAVES,
  SMALL_STAR,
  STAR_STROKE,
  WORDMARK_GROW,
  WORDMARK_HERO,
  WORDMARK_WIDTH,
} from './logo-geometry';

/**
 * Colour tone (A3 «Δάφνη θαλασσί»):
 *  - `brand`   on light or dark surfaces: primary-blue leaves (sea-teal in dark mode), saffron
 *              star, ink wordmark. Theme-aware through the --gh-logo-* tokens.
 *  - `field`   on the deep sea-blue brand field (sidebar, hero, highlight cards, photos):
 *              sea-teal leaves, saffron star, white wordmark. Fixed colours.
 *  - `inverse` deprecated alias of `field`.
 *  - `mono`    everything in currentColor (stamps, single-colour print).
 */
export type LogoTone = 'brand' | 'field' | 'inverse' | 'mono';

interface CommonProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  tone?: LogoTone;
  /** Accessible name. Pass `null` when the logo is decorative next to visible text. */
  title?: string | null;
}

function colours(tone: LogoTone) {
  switch (tone) {
    case 'mono':
      return { leaf: 'currentColor', star: 'currentColor', ink: 'currentColor' };
    case 'field':
    case 'inverse':
      return {
        // Fixed: the field is the same deep blue in light and dark mode.
        leaf: 'var(--gh-field-leaf, #8FD3D0)',
        star: 'var(--gh-field-accent, #E3A23B)',
        ink: 'var(--gh-on-field, #FFFFFF)',
      };
    default:
      return {
        leaf: 'var(--gh-logo-leaf, #1F5A8E)',
        star: 'var(--gh-logo-star, #E3A23B)',
        ink: 'var(--gh-logo-ink, #0E1C2B)',
      };
  }
}

function a11y(title: string | null | undefined) {
  return title === null
    ? ({ 'aria-hidden': true, focusable: false } as const)
    : ({ role: 'img', 'aria-label': title ?? 'GrowHero' } as const);
}

function Mark({ leaf, star, small }: { leaf: string; star: string; small?: boolean }) {
  const leaves = small ? SMALL_LEAVES : MARK_LEAVES;
  return (
    <>
      <g fill={leaf}>
        {leaves.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
      <path
        d={small ? SMALL_STAR : MARK_STAR}
        fill={star}
        stroke={star}
        strokeWidth={STAR_STROKE}
        strokeLinejoin="round"
      />
    </>
  );
}

/**
 * The laurel-and-star mark. Use `variant="small"` (or let `size <= 20` pick it) for the
 * simplified 3-leaf cut that stays crisp at favicon sizes.
 */
export function LogoMark({
  tone = 'brand',
  title,
  size = 32,
  variant,
  ...rest
}: CommonProps & { size?: number | string; variant?: 'full' | 'small' }) {
  const c = colours(tone);
  const small = variant ? variant === 'small' : typeof size === 'number' && size <= 20;
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} {...a11y(title)} {...rest}>
      <Mark leaf={c.leaf} star={c.star} small={small} />
    </svg>
  );
}

/** Mark + "GrowHero" wordmark (Commissioner 800) lockup, or the wordmark alone with `markless`. */
export function Logo({
  tone = 'brand',
  title,
  height = 28,
  markless = false,
  ...rest
}: CommonProps & { height?: number | string; markless?: boolean }) {
  const c = colours(tone);
  if (markless) {
    const w = WORDMARK_WIDTH + 1;
    return (
      <svg viewBox={`0 -33 ${w} 35`} height={height} {...a11y(title)} {...rest}>
        <g fill={c.ink}>
          <path d={WORDMARK_GROW} />
          <path d={WORDMARK_HERO} />
        </g>
      </svg>
    );
  }
  return (
    <svg
      viewBox={`0 0 ${LOCKUP.width} ${LOCKUP.height}`}
      height={height}
      {...a11y(title)}
      {...rest}
    >
      <g transform={`scale(${LOCKUP.markScale})`}>
        <Mark leaf={c.leaf} star={c.star} />
      </g>
      <g transform={`translate(${LOCKUP.textX} ${LOCKUP.baseline})`} fill={c.ink}>
        <path d={WORDMARK_GROW} />
        <path d={WORDMARK_HERO} />
      </g>
    </svg>
  );
}
